import store_portail from '@/store/mods/portail/portail'
import ConfirmerSortieModal from '@/components/portail/confirmer-sortie-modal'
import {
    INTERVALLE_ACTUALISATION_MS, apercuBloc, compterOnglets, donneesPerimees, empreinte, estAgentSecurite,
    etatRecherche, fermerSession, fusionnerVerdicts, genererRequeteId, heureLocale, heureServeur, heureValide, idsCandidats,
    libelleLiberation, libelleNature, libelleNombrePatients, libelleSexeAge, libelleSortie, lireJeton, lireVerdicts,
    messageConflit, messageErreurLecture, nettoyerCandidats, nettoyerSortis, sessionAgentExpiree, statutErreur, tonNature
} from '@/utils/portail-sorties'

// PORT-001 — écran « Contrôle des sorties » (agent de sécurité, administrateur).
// Cycle : capacité portail:read → libérés (be_clinique) → verdicts (be_caisse) → fusion côté navigateur.
// Échec fermé : sans verdict booléen « true », aucun patient n'est affiché comme à jour.
// Les jetons de capacité ne quittent pas la méthode qui les obtient (ni store, ni stockage local).

const documentCache = () => typeof document !== 'undefined' && document.hidden === true

export default {
    name: 'controle-sorties',
    components: { ConfirmerSortieModal },
    data() {
        return {
            est_vivant: true,
            sequence: 0,
            requete_en_cours: false,
            debut_requete: 0,
            is_loading: false,
            liste_chargee: false,
            candidats: [],
            sortis: [],
            verdicts: [],
            verdicts_en_echec: false,
            tronque: false,
            empreinte_liste: '',
            derniere_maj: 0,
            heure_serveur: '',
            maintenant: Date.now(),
            recherche: '',
            onglet: 'a_sortir',
            message_erreur: '',
            message_info: '',
            message_alerte: '',
            message_succes: '',
            session_expiree: false,
            patient_a_confirmer: null,
            requete_id: '',
            demande_en_attente: null,
            is_saving: false,
            message_erreur_modal: '',
            minuteur: null
        }
    },
    computed: {
        fusion() {
            return fusionnerVerdicts(this.candidats, this.verdicts_en_echec ? null : this.verdicts)
        },
        resultat() {
            return etatRecherche(this.recherche, this.fusion, this.sortis)
        },
        compteurs() {
            return compterOnglets(this.fusion, this.sortis)
        },
        lignes_a_sortir() { return this.resultat.a_sortir },
        lignes_sortis() { return this.resultat.sortis },
        lignes_onglet() { return this.onglet === 'sortis' ? this.lignes_sortis : this.lignes_a_sortir },
        // Blocs de recherche limités à 5 noms : le résultat vert reste visible au-dessus du clavier.
        bloc_regulariser() { return apercuBloc(this.resultat.a_regulariser) },
        bloc_indetermines() { return apercuBloc(this.resultat.indetermines) },
        bloc_sortis() { return apercuBloc(this.resultat.sortis) },
        // Patients à laisser sortir trouvés alors que l'écran montre autre chose (bloc rouge, onglet Déjà sortis).
        indication_a_sortir() {
            const resultat = this.resultat
            if (!resultat.active || !resultat.a_sortir.length) return ''
            const nombre = libelleNombrePatients(resultat.a_sortir.length)
            if (this.onglet === 'sortis') return 'Trouvé dans l’onglet À laisser sortir : ' + nombre + '.'
            if (resultat.a_regulariser.length || resultat.indetermines.length) return nombre + ' à laisser sortir ci-dessous.'
            return ''
        },
        donnees_perimees() {
            return this.liste_chargee && donneesPerimees(this.derniere_maj, this.maintenant)
        },
        chargement_visible() { return this.is_loading && !this.liste_chargee },
        // Heure de genere_at (be_clinique) ; l'heure du poste n'est qu'un repli si le serveur ne la fournit pas.
        libelle_maj() { return this.derniere_maj ? 'Mis à jour à ' + (this.heure_serveur || heureLocale(this.derniere_maj)) : '' },
        peut_constater() {
            return this.liste_chargee && !this.donnees_perimees && !this.verdicts_en_echec && !this.is_saving
        },
        peut_confirmer_modal() {
            const patient = this.patient_a_confirmer
            return !!patient && this.liste_chargee && !this.donnees_perimees && !this.verdicts_en_echec &&
                this.fusion.a_sortir.some(item => item.patient_id === patient.patient_id)
        }
    },
    mounted() {
        this.charger(false)
        if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') window.addEventListener('focus', this.au_retour)
        if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') document.addEventListener('visibilitychange', this.au_retour)
        this.minuteur = setInterval(this.tic, INTERVALLE_ACTUALISATION_MS)
    },
    beforeDestroy() {
        this.est_vivant = false
        this.sequence++
        clearInterval(this.minuteur)
        this.minuteur = null
        if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') window.removeEventListener('focus', this.au_retour)
        if (typeof document !== 'undefined' && typeof document.removeEventListener === 'function') document.removeEventListener('visibilitychange', this.au_retour)
    },
    methods: {
        libelle_liberation: libelleLiberation,
        libelle_sexe_age: libelleSexeAge,
        libelle_nature: libelleNature,
        ton_nature: tonNature,
        libelle_sortie: libelleSortie,

        actif(sequence) {
            return this.est_vivant && sequence === this.sequence
        },

        // Actualisation silencieuse toutes les 60 s, suspendue quand l'onglet est masqué.
        tic() {
            if (!this.est_vivant) return
            this.maintenant = Date.now()
            if (this.verifier_session_agent()) return
            if (documentCache() || this.is_saving) return
            return this.charger(true)
        },

        // Retour au premier plan (focus ou visibilitychange) : relance immédiate.
        au_retour() {
            if (!this.est_vivant || documentCache()) return
            this.maintenant = Date.now()
            if (this.verifier_session_agent()) return
            if (this.is_saving) return
            return this.charger(true)
        },

        actualiser() {
            if (this.is_loading || this.is_saving) return
            this.message_succes = ''
            this.message_info = ''
            this.message_alerte = ''
            return this.charger(false)
        },

        // Session d'agent de plus de 12 h : retour à la connexion (le routeur ferme la session).
        verifier_session_agent() {
            const store = this.$store
            if (!store || !store.getters) return false
            const auth_info = store.getters.authInfo
            if (!auth_info || !estAgentSecurite(auth_info, store.getters.activeRole)) return false
            if (!sessionAgentExpiree(auth_info, Date.now())) return false
            this.est_vivant = false
            this.sequence++
            clearInterval(this.minuteur)
            this.minuteur = null
            if (this.$router) this.naviguer('/login')
            return true
        },

        naviguer(chemin) {
            const resultat = this.$router.push({ path: chemin })
            if (resultat && typeof resultat.catch === 'function') resultat.catch(() => {})
            return resultat
        },

        async charger(discret) {
            if (!this.est_vivant) return
            // Une requête bloquée (Wi-Fi du portail) ne suspend pas l'actualisation au-delà de deux cycles.
            if (discret && this.requete_en_cours && Date.now() - this.debut_requete < INTERVALLE_ACTUALISATION_MS * 2) return
            const sequence = ++this.sequence
            this.requete_en_cours = true
            this.debut_requete = Date.now()
            if (!discret) this.is_loading = true
            let etape = 'acces'
            try {
                const acces = await store_portail.getAccesPortail()
                if (!this.actif(sequence)) return
                const jeton = lireJeton(acces)
                if (!jeton) throw new Error('Accès portail indisponible')
                etape = 'liste'
                const liberes = await store_portail.getPatientsLiberes({ auth_id: jeton.auth_id }, jeton.access_token)
                if (!this.actif(sequence)) return
                if (!liberes || liberes.success !== true || !Array.isArray(liberes.candidats) || !Array.isArray(liberes.sortis)) {
                    throw new Error('Liste des libérés indisponible')
                }
                const candidats = nettoyerCandidats(liberes.candidats)
                const sortis = nettoyerSortis(liberes.sortis)
                const patient_ids = idsCandidats(candidats)
                let verdicts = []
                let verdicts_en_echec = false
                if (patient_ids.length) {
                    try {
                        const reponse = await store_portail.getVerdictsPaiement({ auth_id: jeton.auth_id, patient_ids }, jeton.access_token)
                        if (!this.actif(sequence)) return
                        verdicts = lireVerdicts(reponse)
                    } catch (erreur) {
                        if (!this.actif(sequence)) return
                        verdicts = null
                    }
                    if (!verdicts) { verdicts = null; verdicts_en_echec = true }
                }
                this.appliquer({ candidats, sortis, verdicts, verdicts_en_echec, tronque: liberes.tronque === true }, heureServeur(liberes.genere_at))
                this.message_erreur = ''
                this.session_expiree = false
            } catch (erreur) {
                if (!this.actif(sequence)) return
                const statut = statutErreur(erreur)
                this.session_expiree = statut === 401 && etape === 'acces'
                this.message_erreur = messageErreurLecture(statut, etape)
            } finally {
                if (this.actif(sequence)) {
                    this.requete_en_cours = false
                    this.is_loading = false
                }
            }
        },

        // Pas de nouveau rendu si la réponse est identique : seule l'heure de mise à jour avance.
        // derniere_maj (horloge du poste) mesure seulement l'ancienneté (5 min) ; l'heure affichée vient du serveur.
        appliquer(donnees, heure_serveur) {
            const nouvelle = empreinte(donnees)
            this.heure_serveur = typeof heure_serveur === 'string' ? heure_serveur : ''
            this.derniere_maj = Date.now()
            this.maintenant = this.derniere_maj
            this.liste_chargee = true
            if (nouvelle === this.empreinte_liste) return false
            this.empreinte_liste = nouvelle
            this.candidats = donnees.candidats
            this.sortis = donnees.sortis
            this.verdicts = donnees.verdicts
            this.verdicts_en_echec = donnees.verdicts_en_echec
            this.tronque = donnees.tronque
            this.controler_confirmation()
            return true
        },

        // La fenêtre de confirmation se ferme si le patient n'est plus « À laisser sortir ».
        controler_confirmation() {
            const patient = this.patient_a_confirmer
            if (!patient || this.is_saving) return
            if (this.fusion.a_sortir.some(item => item.patient_id === patient.patient_id)) return
            const sorti = this.sortis.find(item => item.patient_id === patient.patient_id)
            if (sorti) {
                this.demande_en_attente = null
                this.message_info = 'Sortie déjà enregistrée pour ' + patient.nom_affiche + (heureValide(sorti.sorti_heure) ? ' à ' + sorti.sorti_heure : '') + '.'
            } else {
                this.message_info = 'La situation de ' + patient.nom_affiche + ' a changé : ne le laissez pas sortir sans nouvelle vérification.'
            }
            this.patient_a_confirmer = null
            this.message_erreur_modal = ''
        },

        choisir_onglet(onglet) {
            this.onglet = onglet === 'sortis' ? 'sortis' : 'a_sortir'
        },

        // Pas de v-model : il attend la fin de la composition du clavier (Gboard, Samsung) avant de filtrer.
        saisir_recherche(event) {
            const cible = event && event.target
            this.recherche = cible && typeof cible.value === 'string' ? cible.value : ''
        },

        effacer_recherche() {
            this.recherche = ''
            if (this.$refs && this.$refs.recherche && typeof this.$refs.recherche.focus === 'function') this.$refs.recherche.focus()
        },

        ouvrir_confirmation(patient) {
            if (!patient || !this.peut_constater || this.patient_a_confirmer) return
            if (!this.fusion.a_sortir.some(item => item.patient_id === patient.patient_id)) return
            const en_attente = this.demande_en_attente
            // Résultat incertain pour ce patient : la même demande est renvoyée (idempotence par requete_id).
            this.requete_id = en_attente && en_attente.patient_id === patient.patient_id ? en_attente.requete_id : genererRequeteId()
            this.message_succes = ''
            this.message_info = ''
            this.message_alerte = ''
            this.message_erreur_modal = ''
            this.patient_a_confirmer = patient
        },

        fermer_confirmation() {
            if (this.is_saving) return
            this.patient_a_confirmer = null
            this.message_erreur_modal = ''
        },

        async confirmer_sortie() {
            const patient = this.patient_a_confirmer
            if (!patient || this.is_saving || !this.est_vivant || !this.peut_confirmer_modal) return
            const requete_id = this.requete_id || genererRequeteId()
            this.requete_id = requete_id
            this.is_saving = true
            this.message_erreur_modal = ''
            let etape = 'acces'
            let recharger = false
            try {
                const acces = await store_portail.getAccesPassagePortail()
                if (!this.est_vivant) return
                const jeton = lireJeton(acces)
                if (!jeton) throw new Error('Accès passage indisponible')
                etape = 'constat'
                // Mémorisée avant l'envoi : toute issue incertaine réutilise le même identifiant.
                this.demande_en_attente = { patient_id: patient.patient_id, requete_id }
                const reponse = await store_portail.constaterSortie({ auth_id: jeton.auth_id, patient_id: patient.patient_id, requete_id }, jeton.access_token)
                if (!this.est_vivant) return
                if (!reponse || reponse.success !== true) throw new Error('Sortie non confirmée')
                const heure = reponse.sortie && heureValide(reponse.sortie.sorti_heure) ? reponse.sortie.sorti_heure : ''
                this.demande_en_attente = null
                this.requete_id = ''
                this.deplacer_vers_sortis(patient, heure)
                this.message_succes = 'Sortie constatée : ' + patient.nom_affiche + (heure ? ' à ' + heure : '') + '.'
                this.is_saving = false
                this.patient_a_confirmer = null
                recharger = true
            } catch (erreur) {
                if (!this.est_vivant) return
                const statut = statutErreur(erreur)
                if (etape === 'acces') {
                    // Aucune constatation envoyée.
                    this.session_expiree = statut === 401
                    this.message_erreur_modal = statut === 401 ? 'Votre session ne peut plus être authentifiée. Reconnectez-vous. Aucune sortie n’a été enregistrée.'
                        : statut === 403 ? 'Votre compte ne dispose pas de l’accès pour constater une sortie. Aucune sortie n’a été enregistrée.'
                            : 'Autorisation indisponible. Aucune sortie n’a été enregistrée. Réessayez.'
                } else if (statut === 409) {
                    this.demande_en_attente = null
                    this.requete_id = ''
                    // Conservé après le rechargement (contrairement à l'erreur de liste, effacée au succès suivant).
                    this.message_alerte = messageConflit(erreur)
                    this.is_saving = false
                    this.patient_a_confirmer = null
                    recharger = true
                } else if (statut === 401 || statut === 403 || statut === 422) {
                    // Refus du service avant enregistrement. Un identifiant refusé (422) n'est pas réutilisé.
                    if (statut === 422) { this.demande_en_attente = null; this.requete_id = '' }
                    this.message_erreur_modal = statut === 422 ? 'Demande refusée par le service. Actualisez la liste puis réessayez.'
                        : statut === 401 ? 'Autorisation expirée. Réessayez.'
                            : 'Accès refusé par le service. Aucune sortie n’a été enregistrée.'
                } else {
                    // Réseau coupé ou erreur serveur : issue inconnue, même requete_id au prochain essai.
                    this.message_erreur_modal = 'Résultat incertain (connexion). Réessayez : la même demande sera renvoyée, sans risque de doublon.'
                    recharger = true
                }
            } finally {
                if (this.est_vivant) this.is_saving = false
            }
            if (recharger && this.est_vivant) await this.charger(false)
        },

        deplacer_vers_sortis(patient, heure) {
            this.candidats = this.candidats.filter(item => item.patient_id !== patient.patient_id)
            this.sortis = [{ patient_id: patient.patient_id, nom_affiche: patient.nom_affiche, dossier: patient.dossier, sorti_heure: heure, constate_par_moi: true }]
                .concat(this.sortis.filter(item => item.patient_id !== patient.patient_id))
            this.empreinte_liste = ''
        },

        se_reconnecter() {
            if (!this.$router) return
            const store = this.$store
            const stockage = typeof localStorage !== 'undefined' ? localStorage : null
            const fermer = () => fermerSession(store, stockage, false)
            const resultat = this.$router.push({ path: '/login' })
            if (resultat && typeof resultat.then === 'function') resultat.then(fermer, fermer)
            else fermer()
        }
    }
}
